import React, { useEffect, useRef } from 'react';
import { Order } from 'models/Order';
import { Filters } from './filters';
import { useOrders } from './useOrders';
import { ColumnsType } from 'antd/es/table';
import { PERMISSIONS } from 'constants/permissions';
import { LoadingOutlined } from '@ant-design/icons';
import { HasAccess } from 'components/common/accessControl';
import { FilterIcon, PlusIcon, ViewIcon } from 'components/common/icons';
import { OrderStatusBadge } from 'components/common/StatusBadge/OrderStatusBadge';
import { Button, Col, Image, Input, PageTitle, Row, Table, Tooltip } from 'components';
import { AddOrderModal } from './addOrder';
import styles from './styles.module.scss';

const { Search } = Input;

interface OrdersProps {
  forDashboard?: boolean;
  onTotalUpdate?: (total: number) => void;
}

export const Orders = React.memo(({ forDashboard, onTotalUpdate }: OrdersProps) => {
  const {
    data,
    isLoading,
    routeChange,
    pagination,
    onSearch,
    closeDrawer,
    openDrawer,
    filterDrawerVisible,
    form,
    handleTableChange,
    applyFilters,
    resetFilters,
    totalElements,
    onAddModalStateChange,
    addModalState,
    // statusFilter,
    // onStatusFilterChange,
    // statusCounts,
  } = useOrders(forDashboard);

  // Track the previous total to avoid unnecessary updates
  const previousTotalRef = useRef<number | undefined>(undefined);

  // Report total count back to parent dashboard component
  useEffect(() => {
    if (forDashboard && onTotalUpdate && totalElements !== undefined && previousTotalRef.current !== totalElements) {
      // Update the ref to track the latest value
      previousTotalRef.current = totalElements;
      onTotalUpdate(totalElements);
    }
  }, [forDashboard, onTotalUpdate, totalElements]);

  const handleAddNew = () => {
    onAddModalStateChange();
  };

  const columns: ColumnsType<Order> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      render: (_, { uuid }) => {
        return uuid;
      },
    },
    {
      title: 'Client',
      dataIndex: 'client',
      key: 'clientName',
      width: 200,
      ellipsis: true,
      render: (client) => {
        if (!client) return '';
        return (
          <Tooltip title={client.name}>
            <Row wrap={false} align={'middle'} className={'gap-8'}>
              {client?.verified && <div style={{ color: '#52c41a', fontSize: '12px' }}>✓</div>}
              <div className={'text-ellipsis'}>{client.name}</div>
            </Row>
          </Tooltip>
        );
      },
    },
    {
      title: 'Delivery Country',
      dataIndex: 'deliveryCountry',
      key: 'deliveryCountryId',
      render: (country) => {
        return (
          <Row wrap={false} align={'middle'} className={'gap-12'}>
            {country?.fileLocation && (
              <Image
                src={country?.fileLocation}
                width={18}
                height={18}
                preview={false}
                style={{ verticalAlign: 'unset', width: '18px' }}
                className={'radius-circle'}
              />
            )}
            <Col>{country?.name}</Col>
          </Row>
        );
      },
    },
    {
      title: 'Created By',
      dataIndex: 'createdBy',
      key: 'createdBy',
      render: (value) => {
        if (value) {
          return `${value.firstName || ''} ${value.lastName || ''}`.trim();
        }
        return '-';
      },
    },
    {
      title: 'Created Date',
      dataIndex: 'formattedCreatedDate',
      key: 'createDate',
      minWidth: 130,
      sorter: true,
      render: (value) => value,
    },
    {
      title: 'Last Updated',
      dataIndex: 'formattedLastUpdated',
      key: 'lastModifiedDate',
      minWidth: 130,
      sorter: true,
      render: (value) => value,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => <OrderStatusBadge type={status} />,
    },
    {
      fixed: 'right',
      width: '36px',
      key: 'actions',
      render: (value) => (
        <Button onClick={() => routeChange(value.id)} type={'text'} size={'small'} icon={<ViewIcon />} />
      ),
    },
  ];

  return (
    <Col span={24} className={styles.main}>
      <Row gutter={[0, 24]}>
        <Col span={24}>
          <Row align={'middle'} justify={'space-between'}>
            <PageTitle title={'Orders'} />
            {forDashboard ? (
              ''
            ) : (
              <HasAccess role={PERMISSIONS.orders.order.write}>
                <Button disabled={true} type={'primary'} onClick={handleAddNew} icon={<PlusIcon />}>
                  Add New
                </Button>
              </HasAccess>
            )}
          </Row>
        </Col>

        {/*{!forDashboard && (*/}
        {/*  <Col span={24}>*/}
        {/*    <StatusFilterButtons*/}
        {/*      activeStatus={statusFilter}*/}
        {/*      onStatusChange={onStatusFilterChange}*/}
        {/*      orderCounts={statusCounts}*/}
        {/*    />*/}
        {/*  </Col>*/}
        {/*)}*/}

        <Col span={24} id={'orders_list'} className={styles.main_container}>
          <Row>
            <Col span={24}>
              <Row justify={'end'} className={styles.actions_container}>
                <Col style={{ width: '300px' }}>
                  <Search enterButton={false} placeholder={'Search'} onChange={(e) => onSearch(e.target.value)} />
                </Col>
                <Button color="default" variant="filled" onClick={openDrawer} icon={<FilterIcon />} />
              </Row>
            </Col>
            <Col span={24} style={{ paddingTop: '20px' }}>
              <HasAccess role={PERMISSIONS.orders.order.read}>
                <Table
                  tableLayout={'auto'}
                  bordered
                  scroll={{
                    x: true,
                  }}
                  rowKey="id"
                  size={'small'}
                  dataSource={data}
                  columns={columns}
                  loading={{
                    indicator: <LoadingOutlined style={{ fontSize: 48 }} spin />,
                    spinning: isLoading,
                  }}
                  locale={
                    isLoading
                      ? { emptyText: null } // Hide "No Data" text while loading
                      : {} // Use default AntD "No Data" icon when empty
                  }
                  onChange={handleTableChange}
                  pagination={{
                    position: ['bottomCenter'],
                    showSizeChanger: true,
                    showTotal: (total) => `Total ${total} items`,
                    size: 'default',
                    current: pagination.page,
                    pageSize: pagination.size,
                    total: totalElements,
                  }}
                />
              </HasAccess>
            </Col>
          </Row>
        </Col>
      </Row>
      <Filters
        visible={filterDrawerVisible}
        onClose={closeDrawer}
        form={form}
        onApply={applyFilters}
        onReset={resetFilters}
        // statusFilter={statusFilter}
      />
      <AddOrderModal modalState={addModalState} onModalStateChange={onAddModalStateChange} />
    </Col>
  );
});
